import type { CSSProperties, FC, KeyboardEvent, ReactNode } from 'react'
import type { HeroAnnotation } from '@/types'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { ArrowDown, ArrowUpRight, Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import CoverFrame from '@/components/generals/cover-frame'
import CountUp from '@/components/generals/count-up'

import {
    HERO_DURATION_MS,
    HERO_IMAGE_RATIO,
    HERO_VIEWS
} from '@/constants/landing'

interface AnnotationProps {
    annotation: HeroAnnotation
    active: boolean
    delay: number
}

const Annotation: FC<AnnotationProps> = ({ annotation, active, delay }): ReactNode => {
    const onRight = annotation.side === 'right'
    const style: CSSProperties = { left: `${annotation.x}%`, top: `${annotation.y}%` }
    const timing: CSSProperties = { transitionDelay: active ? `${delay}ms` : '0ms' }

    return (
        <div style={style} className='absolute size-0'>
            <span
                style={timing}
                className={cn(
                    'absolute -left-[7px] -top-[7px] size-3.5 rounded-full border border-ember/80 bg-ember/30 transition-[opacity,scale] duration-500',
                    active ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                )}
            />

            <span
                className={cn(
                    'absolute -left-[7px] -top-[7px] size-3.5 rounded-full bg-ember/50',
                    active ? 'animate-ping-soft' : 'opacity-0'
                )}
            />

            <span className='absolute -left-[3px] -top-[3px] size-1.5 rounded-full bg-ember' />

            <span
                style={timing}
                className={cn(
                    'absolute top-0 h-px w-12 from-ember/90 to-ember/30 transition-[scale] duration-700 ease-out',
                    onRight ? 'left-2.5 origin-left bg-gradient-to-r' : 'right-2.5 origin-right bg-gradient-to-l',
                    active ? 'scale-x-100' : 'scale-x-0'
                )}
            />

            <div
                style={{ transitionDelay: active ? `${delay + 250}ms` : '0ms' }}
                className={cn(
                    'absolute top-0 w-max max-w-[15rem] -translate-y-1/2 rounded-xl border border-white/15 bg-ink/55 px-3.5 py-2.5 shadow-[0_12px_40px_-12px_oklch(0_0_0/0.6)] backdrop-blur-md transition-[opacity,translate] duration-700 ease-out',
                    onRight ? 'left-[3.75rem]' : 'right-[3.75rem]',
                    active ? 'translate-x-0 opacity-100' : cn('opacity-0', onRight ? '-translate-x-2' : 'translate-x-2')
                )}
            >
                <p className='font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-ember'>
                    {annotation.title}
                </p>

                <p className='mt-1 text-[13px] leading-snug text-ink-foreground/90'>
                    {annotation.detail}
                </p>
            </div>
        </div>
    )
}

interface VisionOverlayProps {
    active: boolean
}

const VisionOverlay: FC<VisionOverlayProps> = ({ active }): ReactNode => {
    return (
        <div className='pointer-events-none absolute inset-0 hidden md:block' aria-hidden='true'>
            <div className='absolute inset-y-0 left-[38%] right-[8%] overflow-hidden'>
                <div className={cn('absolute inset-0', active && 'animate-scan')}>
                    <div className='absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-ember/10' />
                    <div className='absolute inset-x-0 bottom-0 h-px bg-ember/50' />
                </div>
            </div>

            <svg
                viewBox='0 0 100 100'
                preserveAspectRatio='none'
                className='absolute inset-0 size-full'
            >
                <line
                    x1='62.5'
                    y1='40'
                    x2='56'
                    y2='59'
                    pathLength={1}
                    strokeDasharray='0.02 0.018'
                    vectorEffect='non-scaling-stroke'
                    className={cn('stroke-ember/80 transition-opacity delay-500 duration-700', active ? 'opacity-100' : 'opacity-0')}
                    strokeWidth='1.25'
                />
            </svg>

            <div
                className={cn(
                    'absolute left-[48.5%] top-[59.5%] h-[22.5%] w-[13%] transition-[opacity,scale] delay-300 duration-700 ease-out',
                    active ? 'scale-100 opacity-100' : 'scale-110 opacity-0'
                )}
            >
                <span className='absolute left-0 top-0 size-4 border-l-[1.5px] border-t-[1.5px] border-ember' />
                <span className='absolute right-0 top-0 size-4 border-r-[1.5px] border-t-[1.5px] border-ember' />
                <span className='absolute bottom-0 left-0 size-4 border-b-[1.5px] border-l-[1.5px] border-ember' />
                <span className='absolute bottom-0 right-0 size-4 border-b-[1.5px] border-r-[1.5px] border-ember' />

                <div className='absolute -top-2 left-0 flex -translate-y-full items-center gap-2 rounded-md bg-ember px-2 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink'>
                    Mug · stoneware
                    <span className='text-ink/60'>
                        0.97
                    </span>
                </div>

                <div className='absolute -bottom-2 left-0 translate-y-full whitespace-nowrap rounded-md border border-white/15 bg-ink/60 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-foreground/85 backdrop-blur-md'>
                    Grasp by handle · 340 g
                </div>
            </div>
        </div>
    )
}

const HeroShowcase: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const mediaRefs = useRef<Array<HTMLDivElement | null>>([])
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const [active, setActive] = useState(0)
    const [cycle, setCycle] = useState(0)
    const [paused, setPaused] = useState(false)
    const [held, setHeld] = useState(false)
    const [visible, setVisible] = useState(true)
    const reducedMotion = useReducedMotion()

    const running = !paused && !held && visible && !reducedMotion
    const view = HERO_VIEWS[active]

    useEffect(() => {
        const node = sectionRef.current

        if (!node) return

        let inViewport = true

        const update = () => setVisible(inViewport && document.visibilityState === 'visible')

        const observer = new IntersectionObserver(([entry]) => {
            inViewport = entry.isIntersecting
            update()
        }, { threshold: 0.25 })

        observer.observe(node)
        document.addEventListener('visibilitychange', update)

        return () => {
            observer.disconnect()
            document.removeEventListener('visibilitychange', update)
        }
    }, [])

    useEffect(() => {
        const node = sectionRef.current

        if (!node || !window.matchMedia('(pointer: fine)').matches) return

        let frame = 0

        const onMove = (event: PointerEvent) => {
            cancelAnimationFrame(frame)

            frame = requestAnimationFrame(() => {
                const rect = node.getBoundingClientRect()
                const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
                const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))

                node.style.setProperty('--px', (x * 2 - 1).toFixed(3))
                node.style.setProperty('--py', (y * 2 - 1).toFixed(3))
                node.style.setProperty('--gx', `${(x * 100).toFixed(2)}%`)
                node.style.setProperty('--gy', `${(y * 100).toFixed(2)}%`)
            })
        }

        const onLeave = () => {
            node.style.setProperty('--px', '0')
            node.style.setProperty('--py', '0')
        }

        node.addEventListener('pointermove', onMove)
        node.addEventListener('pointerleave', onLeave)

        return () => {
            cancelAnimationFrame(frame)
            node.removeEventListener('pointermove', onMove)
            node.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    useEffect(() => {
        const media = mediaRefs.current[active]

        if (!media) return

        media.style.animation = 'none'
        void media.offsetWidth
        media.style.animation = ''
    }, [active, cycle])

    const select = (index: number) => {
        setActive(index)
        setCycle(value => value + 1)
    }

    const advance = () => select((active + 1) % HERO_VIEWS.length)

    const onTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
        const last = HERO_VIEWS.length - 1
        let next = -1

        if (event.key === 'ArrowRight') next = active === last ? 0 : active + 1
        if (event.key === 'ArrowLeft') next = active === 0 ? last : active - 1
        if (event.key === 'Home') next = 0
        if (event.key === 'End') next = last

        if (next < 0) return

        event.preventDefault()
        select(next)
        tabRefs.current[next]?.focus()
    }

    return (
        <section
            id='hero'
            ref={sectionRef}
            aria-label='Oriel, a humanoid robot for the home'
            className='relative isolate h-[100svh] min-h-[40rem] overflow-hidden bg-ink text-ink-foreground'
        >
            {HERO_VIEWS.map((item, index) => {
                const isActive = index === active

                return (
                    <div
                        key={item.id}
                        id={`hero-panel-${item.id}`}
                        role='tabpanel'
                        aria-labelledby={`hero-tab-${item.id}`}
                        aria-hidden={!isActive}
                        inert={!isActive}
                        className={cn(
                            'absolute inset-0 transition-opacity ease-[cubic-bezier(0.4,0,0.2,1)]',
                            isActive ? 'z-[1] opacity-100 duration-[1400ms]' : 'z-0 opacity-0 delay-[1400ms] duration-0'
                        )}
                    >
                        <div className='hero-parallax absolute inset-0'>
                            <CoverFrame
                                ratio={HERO_IMAGE_RATIO}
                                subjectX={item.subjectX}
                                subjectY={item.subjectY}
                                targetX={item.targetX}
                                targetXWide={item.targetXWide}
                                targetY={0.46}
                            >
                                <div
                                    ref={node => {
                                        mediaRefs.current[index] = node
                                    }}
                                    className='absolute inset-0 animate-ken-burns'
                                >
                                    <Image
                                        src={item.src}
                                        alt={item.alt}
                                        fill
                                        priority={index === 0}
                                        sizes='(max-aspect-ratio: 16/9) 180vh, 100vw'
                                        className='object-cover'
                                    />
                                </div>

                                {item.id === 'vision' && <VisionOverlay active={isActive} />}

                                <div className='absolute inset-0 hidden lg:block'>
                                    {item.annotations.map((annotation, annotationIndex) => (
                                        <Annotation
                                            key={annotation.title}
                                            annotation={annotation}
                                            active={isActive}
                                            delay={700 + annotationIndex * 220}
                                        />
                                    ))}
                                </div>
                            </CoverFrame>
                        </div>
                    </div>
                )
            })}

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 z-[2]'>
                <div className='absolute inset-0 animate-breathe bg-[radial-gradient(55%_45%_at_82%_18%,oklch(0.8_0.12_68/0.16),transparent_70%)] mix-blend-screen' />
                <div className='hero-light absolute inset-0 hidden md:block' />
                <div className='absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/70 to-transparent' />
                <div className='absolute inset-0 bg-[linear-gradient(to_top,oklch(0.165_0.01_55/0.95)_0%,oklch(0.165_0.01_55/0.7)_32%,transparent_62%)] lg:bg-[linear-gradient(to_top,oklch(0.165_0.01_55/0.9)_0%,transparent_38%),linear-gradient(to_right,oklch(0.165_0.01_55/0.82)_0%,oklch(0.165_0.01_55/0.35)_36%,transparent_56%)]' />
                <div className='absolute inset-0 bg-[radial-gradient(120%_90%_at_60%_40%,transparent_55%,oklch(0.1_0.01_55/0.55)_100%)]' />
                <div className='film-grain absolute inset-0 opacity-[0.14]' />
            </div>

            <div className='relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-5 pb-5 pt-24 md:px-8 md:pb-8'>
                <div className='flex items-end justify-between gap-10'>
                    <div className='max-w-[37rem] animate-rise'>
                        <p className='inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 py-1.5 pl-2.5 pr-3.5 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-foreground/80 backdrop-blur-md'>
                            <span className='relative flex size-2'>
                                <span className='absolute inset-0 animate-ping-soft rounded-full bg-ember' />
                                <span className='relative size-2 rounded-full bg-ember' />
                            </span>

                            Home humanoid · Reserving now
                        </p>

                        <h1 className='mt-5 font-heading text-[clamp(3.6rem,9vw,8.25rem)] leading-[0.88] tracking-[-0.025em]'>
                            Quietly <em className='italic text-ember'>capable.</em>
                        </h1>

                        <p className='mt-5 max-w-[30rem] text-[1.0625rem] leading-relaxed text-ink-foreground/80 md:text-lg'>
                            Oriel is a humanoid robot built for real homes. It moves with care, sees what needs doing, and lends a steady pair of hands.
                        </p>

                        <div className='mt-7 flex flex-wrap items-center gap-3'>
                            <a
                                href='#reserve'
                                className='group inline-flex h-12 items-center gap-2 rounded-full bg-ink-foreground pl-6 pr-5 text-[15px] font-medium text-ink transition-colors hover:bg-ember focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember'
                            >
                                Reserve Oriel
                                <ArrowUpRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5' />
                            </a>

                            <a
                                href='#everyday'
                                className='group inline-flex h-12 items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 text-[15px] text-ink-foreground backdrop-blur-md transition-colors hover:bg-white/12 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember'
                            >
                                See a day at home
                                <ArrowDown className='size-4 transition-transform duration-300 group-hover:translate-y-0.5' />
                            </a>
                        </div>
                    </div>

                    <div
                        className='hidden w-[17.5rem] shrink-0 rounded-2xl border border-white/12 bg-ink/45 p-5 backdrop-blur-xl xl:block'
                    >
                        <div className='flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-ink-foreground/60'>
                            <span className='flex items-center gap-2'>
                                <span className='size-1.5 rounded-full bg-ember' />
                                Live readout
                            </span>

                            <span className='text-ember'>
                                {view.label}
                            </span>
                        </div>

                        <dl className='mt-4 divide-y divide-white/10'>
                            {view.readouts.map(readout => (
                                <div key={`${view.id}-${readout.label}`} className='flex items-baseline justify-between py-2.5'>
                                    <dt className='text-[13px] text-ink-foreground/65'>
                                        {readout.label}
                                    </dt>

                                    <dd className='font-heading text-[1.75rem] leading-none'>
                                        <CountUp value={readout.value} decimals={readout.decimals} runKey={`${active}-${cycle}`} />

                                        {readout.unit && (
                                            <span className='ml-1 font-mono text-[11px] text-ink-foreground/60'>
                                                {readout.unit}
                                            </span>
                                        )}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>

                <div
                    className='mt-8 flex items-start gap-3 md:mt-12 md:gap-5'
                    onPointerEnter={() => setHeld(true)}
                    onPointerLeave={() => setHeld(false)}
                    onFocus={() => setHeld(true)}
                    onBlur={event => {
                        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHeld(false)
                    }}
                >
                    <button
                        type='button'
                        onClick={() => setPaused(value => !value)}
                        aria-label={paused ? 'Play the showcase' : 'Pause the showcase'}
                        className='mt-1 inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/5 text-ink-foreground backdrop-blur-md transition-colors hover:bg-white/12 focus-visible:outline-2 focus-visible:outline-ember md:size-11'
                    >
                        {paused ? <Play className='size-4' /> : <Pause className='size-4' />}
                    </button>

                    <div
                        role='tablist'
                        aria-label='Ways to see Oriel'
                        onKeyDown={onTabKey}
                        className='grid flex-1 grid-cols-4 gap-2 md:gap-5'
                    >
                        {HERO_VIEWS.map((item, index) => {
                            const selected = index === active

                            return (
                                <button
                                    key={item.id}
                                    ref={node => {
                                        tabRefs.current[index] = node
                                    }}
                                    id={`hero-tab-${item.id}`}
                                    type='button'
                                    role='tab'
                                    aria-selected={selected}
                                    aria-controls={`hero-panel-${item.id}`}
                                    tabIndex={selected ? 0 : -1}
                                    onClick={() => select(index)}
                                    className='group relative min-w-0 rounded-sm pb-1 pt-4 text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ember'
                                >
                                    <span className='absolute inset-x-0 top-0 h-[2px] overflow-hidden rounded-full bg-white/15'>
                                        {selected && (reducedMotion ? (
                                            <span className='absolute inset-0 bg-ember' />
                                        ) : (
                                            <span
                                                key={`${active}-${cycle}`}
                                                onAnimationEnd={advance}
                                                style={{
                                                    animation: `hero-progress ${HERO_DURATION_MS}ms linear forwards`,
                                                    animationPlayState: running ? 'running' : 'paused'
                                                }}
                                                className='absolute inset-0 origin-left bg-ember'
                                            />
                                        ))}
                                    </span>

                                    <span className='flex items-baseline gap-2'>
                                        <span className={cn('font-mono text-[11px] transition-colors', selected ? 'text-ember' : 'text-ink-foreground/45')}>
                                            {item.index}
                                        </span>

                                        <span className={cn('truncate text-sm font-medium transition-colors md:text-[15px]', selected ? 'text-ink-foreground' : 'text-ink-foreground/60 group-hover:text-ink-foreground/90')}>
                                            {item.label}
                                        </span>
                                    </span>

                                    <span className={cn('mt-1 hidden text-[13px] leading-snug transition-colors md:line-clamp-1', selected ? 'text-ink-foreground/75' : 'text-ink-foreground/45')}>
                                        {item.caption}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HeroShowcase
